import { Card, Row, Col, Tag, Popconfirm, App } from "antd";
import {
  CheckCircleFilled,
  MessageOutlined,
  UserOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import CustomButton from "../../components/CustomButton/Button";
import BackButton from "../../components/CustomButton/BackButton";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect } from "react";
import { DateFormatter } from "../../helpers/helpers";
import {
  getQuestionnaireThreads,
  deleteQuestionnaireThread,
  getBusinessStatus,
  completeQuestionnaire,
} from "../../redux/slices/QuestionBankSlice";
import ThreadStatusTag from "../../components/ThreadStatusTag/ThreadStatusTag";
import { isThreadCompleted } from "../../helpers/threadStatus";
import "./EnqMngt.scss";

const SendQuestionnaire = () => {
  const { message } = App.useApp();
  const { questionnaireThreads, businessStatus, completeQuestionnaireLoading } =
    useSelector((state) => state.questionBank);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const location = useLocation();
  const { org, isCreditAssessment, fromDeals, dealId, dealCategoryId } =
    location.state || {};
  const isFromDeals =
    fromDeals ||
    new URLSearchParams(location.search).get("fromDeals") === "true";

  const resolvedDealCategoryId =
    org?.dealCategoryId ||
    dealCategoryId ||
    questionnaireThreads?.orgData?.dealCategoryId;

  useEffect(() => {
    dispatch(
      getQuestionnaireThreads({
        orgId,
        dealId,
        dealCategoryId: resolvedDealCategoryId,
      }),
    );
    dispatch(getBusinessStatus({ orgId }));
  }, [dealId, dispatch, orgId, resolvedDealCategoryId]);

  const handleDeleteThread = async (threadId) => {
    const res = await dispatch(deleteQuestionnaireThread(threadId));
    if (res.payload.statusCode === 200) {
      message.success("Thread deleted successfully");
      dispatch(
        getQuestionnaireThreads({
          orgId,
          dealId,
          dealCategoryId: resolvedDealCategoryId,
        }),
      );
    }
  };

  const handleCompleteQuestionnaire = async () => {
    try {
      const payload = {
        dealId,
      };
      const res = await dispatch(completeQuestionnaire(payload));
      if (res.payload?.statusCode === 201) {
        message.success("Questionnaire completed successfully");

        // ✅ Always go to Deals Management
        navigate("/deals-management");
      } else {
        message.error("Failed to complete questionnaire");
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const allThreadsCompleted =
    questionnaireThreads &&
    questionnaireThreads?.threads?.length > 0 &&
    questionnaireThreads?.threads?.every((t) => isThreadCompleted(t));

  const isJV = questionnaireThreads?.orgData?.dealCategoryId === "DCT1000002";

  // If coming from deals management, show only questionnaire management section
  if (isFromDeals) {
    return (
      <div className="onboarding-container">
        <BackButton to="/deals-management" />
        <Row gutter={20} className="bottom-section">
          <Col xs={24} md={14}>
            <Card
              title="Questionnaire Management"
              extra={
                <CustomButton
                  type="primary"
                  onClick={() =>
                    navigate(
                      `/enquiry-management/send-questionnaire-form/${orgId}?dealCategoryId=${encodeURIComponent(
                        resolvedDealCategoryId || "",
                      )}${dealId ? `&dealId=${encodeURIComponent(dealId)}` : ""}${
                        isFromDeals ? "&fromDeals=true" : ""
                      }`,
                      {
                        state: {
                          orgData: questionnaireThreads?.orgData,
                          org:
                            org ||
                            questionnaireThreads?.orgData || {
                              orgId,
                              dealCategoryId: resolvedDealCategoryId,
                            },
                          isCreditAssessment,
                          fromDeals: true,
                          dealId: dealId,
                          dealCategoryId: resolvedDealCategoryId,
                          questionnaireThreads: questionnaireThreads?.threads,
                        },
                      },
                    )
                  }
                >
                  Send Questionnaire
                </CustomButton>
              }
            >
              <div className="questionnaire-list">
                {questionnaireThreads &&
                questionnaireThreads?.threads?.length > 0 ? (
                  questionnaireThreads?.threads?.map((item, index) => {
                    return (
                      <Card
                        key={item.threadId || index}
                        className="questionnaire-card"
                      >
                        <div className="questionnaire-header">
                          <div>
                            <h4>{item.threadName}</h4>
                            <p>
                              {item.questions.length} questions • Sent on{" "}
                              {DateFormatter(item.sentAt)}
                            </p>
                          </div>
                          <ThreadStatusTag thread={item} />
                        </div>
                        <div className="questionnaire-footer">
                          <CustomButton
                            type="default"
                            onClick={() =>
                              navigate(
                                `/enquiry-management/view-thread/${orgId}/${item.threadId}`,
                                {
                                  state: {
                                    orgId,
                                    threadId: item.threadId,
                                    isCreditAssessment: item.isCreditAssessment,
                                  },
                                },
                              )
                            }
                          >
                            View Thread
                          </CustomButton>
                          <Popconfirm
                            title="Delete Thread"
                            description="Are you sure you want to delete this thread?"
                            onConfirm={() => handleDeleteThread(item.threadId)}
                            onCancel={() => {}}
                            okText="Yes"
                            cancelText="No"
                            disabled={isThreadCompleted(item)}
                          >
                            <CustomButton
                              type="default"
                              danger
                              icon={<DeleteOutlined />}
                              disabled={isThreadCompleted(item)}
                            >
                              Delete
                            </CustomButton>
                          </Popconfirm>
                        </div>
                      </Card>
                    );
                  })
                ) : (
                  <div className="empty-box">
                    <MessageOutlined className="empty-icon" />
                    <p>No questionnaires sent yet</p>
                    <span>Click "Send Questionnaire" to begin the process</span>
                  </div>
                )}
              </div>
              {questionnaireThreads &&
                questionnaireThreads?.threads?.length > 0 && (
                  <div className="questionnaire-actions">
                    <CustomButton
                      type="default"
                      className="review-btn"
                      onClick={() =>
                        navigate(`/enquiry-management/review-stage/${orgId}`, {
                          state: {
                            questionnaireThreads: questionnaireThreads?.threads,
                            isCreditAssessment,
                          },
                        })
                      }
                    >
                      ☑ Review All questionnaireThreads
                    </CustomButton>
                    <CustomButton
                      type="primary"
                      className="move-btn"
                      disabled={!allThreadsCompleted}
                      loading={completeQuestionnaireLoading}
                      onClick={handleCompleteQuestionnaire}
                    >
                      Complete Questionnaire
                    </CustomButton>
                  </div>
                )}
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <Card title="Deal Information">
              <p>
                <b>Business Name</b>
                <br />
                {questionnaireThreads?.orgData?.busName}
              </p>
              <p>
                <b>Contact Person</b>
                <br />
                {questionnaireThreads?.orgData?.contactName}
              </p>
              <p>
                <b>Contact Phone number</b>
                <br />
                {questionnaireThreads?.orgData?.contactPhoneNumber}
              </p>
              <p>
                <b>Contact Email</b>
                <br />
                {questionnaireThreads?.orgData?.email}
              </p>
              <p>
                <b>Deal Type</b>
              </p>
              <Tag color="cyan">
                {questionnaireThreads?.orgData?.dealCategoryName}
              </Tag>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="onboarding-container">
      {/* Header */}
      <div className="top-header">
        <CustomButton
          type="text"
          onClick={() => navigate("/enquiry-management")}
        >
          ← Back
        </CustomButton>

        <div>
          <h3>{questionnaireThreads?.orgData?.busName}</h3>
          <p>
            {isCreditAssessment
              ? "Credit Assessment"
              : "Business Onboarding Questionnaire"}
          </p>
        </div>
      </div>

      {/* Top Progress */}
      {!isJV && (
        <Card className="progress-wrapper">
          <div className="main-steps">
            <div className="step">
              {isCreditAssessment ? (
                <CheckCircleFilled className="icon green" />
              ) : (
                <div className="circle blue">1</div>
              )}
              <p>Onboarding Progress</p>
            </div>

            <div className="dotted-line" />

            <div className="step">
              {businessStatus?.Credit_Assessment_Complete ? (
                <CheckCircleFilled className="icon green" />
              ) : (
                <div
                  className={`circle ${isCreditAssessment ? "blue" : "grey"}`}
                >
                  2
                </div>
              )}
              <p>Credit Assessment</p>
            </div>
          </div>

          {/* Sub Steps */}
          <div className="sub-steps">
            {isCreditAssessment ? (
              <div className="sub-step">
                {businessStatus?.Credit_Assessment_Complete ? (
                  <CheckCircleFilled className="icon green" />
                ) : (
                  <MessageOutlined className="icon grey" />
                )}
                <span>Enterprise onboarding and Credit limit Setup</span>
              </div>
            ) : (
              <>
                <div className="sub-step completed">
                  <CheckCircleFilled className="icon green" />
                  <span>Basic Information (New Enquiry)</span>
                </div>

                <div className="line" />

                <div className="sub-step">
                  {businessStatus?.KYC_Verified ? (
                    <CheckCircleFilled className="icon green" />
                  ) : (
                    <MessageOutlined className="icon grey" />
                  )}
                  <span>Complete KYC</span>
                </div>
              </>
            )}

            <div className="line" />
            {!isCreditAssessment && (
              <div className="sub-step">
                {businessStatus?.KYC_Verified ? (
                  <CheckCircleFilled className="icon green" />
                ) : (
                  <UserOutlined className="icon grey" />
                )}
                <span>Approval</span>
              </div>
            )}
            {isCreditAssessment && (
              <div className="sub-step">
                {businessStatus?.Credit_Assessment_Complete &&
                businessStatus?.Business_Active ? (
                  <CheckCircleFilled className="icon green" />
                ) : (
                  <UserOutlined className="icon grey" />
                )}
                <span>Approval</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Bottom Section */}
      <Row gutter={20} className="bottom-section">
        {/* Questionnaire Management */}
        <Col xs={24} md={14}>
          <Card
            title="Questionnaire Management"
            extra={
              <CustomButton
                type="primary"
                onClick={() =>
                  navigate(
                    `/enquiry-management/send-questionnaire-form/${orgId}?dealCategoryId=${encodeURIComponent(
                      resolvedDealCategoryId || "",
                    )}${dealId ? `&dealId=${encodeURIComponent(dealId)}` : ""}${
                      isFromDeals ? "&fromDeals=true" : ""
                    }`,
                    {
                      state: {
                        questionnaireThreads: questionnaireThreads?.threads,
                        isCreditAssessment,
                        org:
                          org ||
                          questionnaireThreads?.orgData || {
                            orgId,
                            dealCategoryId: resolvedDealCategoryId,
                          },
                        orgData: questionnaireThreads?.orgData,
                        dealId,
                        dealCategoryId: resolvedDealCategoryId,
                        fromDeals: isFromDeals,
                      },
                    },
                  )
                }
              >
                Send Questionnaire
              </CustomButton>
            }
          >
            <div className="questionnaire-list">
              {questionnaireThreads &&
              questionnaireThreads?.threads?.length > 0 ? (
                questionnaireThreads?.threads?.map((item, index) => {
                  return (
                    <Card
                      key={item.threadId || index}
                      className="questionnaire-card"
                    >
                      <div className="questionnaire-header">
                        <div>
                          <h4>{item.threadName}</h4>
                          <p>
                            {item.questions.length} questions • Sent on{" "}
                            {DateFormatter(item.sentAt)}
                          </p>
                        </div>

                        <ThreadStatusTag thread={item} />
                      </div>

                      <div className="questionnaire-footer">
                        <CustomButton
                          type="default"
                          onClick={() =>
                            navigate(
                              `/enquiry-management/view-thread/${orgId}/${item.threadId}`,
                              {
                                state: {
                                  orgId,
                                  threadId: item.threadId,
                                  isCreditAssessment: item.isCreditAssessment,
                                },
                              },
                            )
                          }
                        >
                          View Thread
                        </CustomButton>

                        <Popconfirm
                          title="Delete Thread"
                          description="Are you sure you want to delete this thread?"
                          onConfirm={() => handleDeleteThread(item.threadId)}
                          onCancel={() => {}}
                          okText="Yes"
                          cancelText="No"
                          disabled={isThreadCompleted(item)}
                        >
                          <CustomButton
                            type="default"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={isThreadCompleted(item)}
                          >
                            Delete
                          </CustomButton>
                        </Popconfirm>
                      </div>
                    </Card>
                  );
                })
              ) : (
                <div className="empty-box">
                  <MessageOutlined className="empty-icon" />
                  <p>No questionnaires sent yet</p>
                  <span>
                    Click "Send Questionnaire" to begin the onboarding process
                  </span>
                </div>
              )}
            </div>
            {/* Show Buttons Only If questionnaires Exist */}
            {questionnaireThreads &&
              questionnaireThreads?.threads?.length > 0 && (
                <div className="questionnaire-actions">
                  <CustomButton
                    type="default"
                    className="review-btn"
                    onClick={() =>
                      navigate(`/enquiry-management/review-stage/${orgId}`, {
                        state: {
                          questionnaireThreads: questionnaireThreads?.threads,
                          orgData: questionnaireThreads?.orgData,
                          isCreditAssessment,
                        },
                      })
                    }
                  >
                    ☑ Review All questionnaireThreads
                  </CustomButton>

                  {!isJV && (
                    <CustomButton
                      type="primary"
                      className="move-btn"
                      disabled={!allThreadsCompleted}
                      onClick={() => {
                        if (isCreditAssessment) {
                          navigate("/enterprise");
                        } else {
                          navigate(`/enquiry-management/complete-kyc/${orgId}`);
                        }
                      }}
                    >
                      {isCreditAssessment
                        ? "Move to Enterprise"
                        : "Move to KYC Step"}
                    </CustomButton>
                  )}
                </div>
              )}
          </Card>
        </Col>

        {/* Business Info */}
        <Col xs={24} md={10}>
          <Card title="Business Information">
            <p>
              <b>Contact Person</b>
              <br />
              {questionnaireThreads?.orgData?.contactName}
            </p>
            <p>
              <b>Contact Phone number</b>
              <br />
              {questionnaireThreads?.orgData?.contactPhoneNumber}
            </p>
            <p>
              <b>Contact Email</b>
              <br />
              {questionnaireThreads?.orgData?.email}
            </p>
            <p>
              <b>Deal Type</b>
            </p>
            <Tag color="cyan">
              {questionnaireThreads?.orgData?.dealCategoryName}
            </Tag>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SendQuestionnaire;
